import { useMemo, type FC } from "react";

import {
  formatDate,
  isTodayDate,
  isValidDateTime,
  MAIN_DATE_FORMAT,
  NO_VALUE_DISPLAY,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { type IRenderColumnTemplate } from "./_core";
import { DatetimeCompactReadonlyStyled } from "./datetime-compact-readonly-cell.styled";

export const DatetimeCompactReadonlyCellTemplate: FC<IRenderColumnTemplate> = ({
  value,
}) => {
  const { timezone } = useTimezone();
  const { formatDateTimeNormalized, formatTimeNormalized } = useFormats();

  const display = useMemo(() => {
    if (!value || !isValidDateTime(value)) {
      return null;
    }

    const dateISO = value as TDateISO;

    const showDatePart = !isTodayDate(dateISO, timezone);
    const datePart = showDatePart
      ? formatDate({
          dateISO,
          dateFormat: MAIN_DATE_FORMAT,
          timezone,
        })
      : null;
    const timePart = formatTimeNormalized(dateISO);
    const tooltip = formatDateTimeNormalized(dateISO);

    return { datePart, timePart, tooltip };
  }, [formatDateTimeNormalized, formatTimeNormalized, timezone, value]);

  if (!display) {
    return <span>{NO_VALUE_DISPLAY}</span>;
  }

  return (
    <FramerTooltip content={display.tooltip} rtlStrippedBottomLeftPlacement>
      <DatetimeCompactReadonlyStyled>
        {display.datePart ? (
          <span className="datetime-compact-readonly__date">
            {display.datePart}
          </span>
        ) : null}
        <span className="datetime-compact-readonly__time">
          {display.timePart}
        </span>
      </DatetimeCompactReadonlyStyled>
    </FramerTooltip>
  );
};
